import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { ViewTitle } from '../../components/ViewTitle';
import * as actions from '../../redux/actions';

const PAYMENT_TYPES = ['เงินสด', 'โอน', 'เงินรวม'];

const SumPaymentGold = ({ title, subtitle }) => {
  const { goldTransaction, paymentOther, me } = useSelector((state) => ({
    goldTransaction: state.gold,
    paymentOther: state.paymentOther,
    me: state.me,
  }));

  const dispatch = useDispatch();
  const history = useHistory();

  const [amount, setAmount] = useState('');
  const [listPayment, setListPayment] = useState('');
  const [date, setDate] = useState(dayjs());
  const bank = useSelector((state) => state.bank);

  const cashBank = _.find(bank?.rows, (b) => b?.isCash);
  // Helper functions
  const formatNumber = (number) =>
    new Intl.NumberFormat('th-TH', { style: 'decimal' }).format(number || 0);

  const calculateTotals = () => {
    const otherInCash = _.sumBy(paymentOther?.rows, (row) =>
      row?.typeTansaction === 'รายรับ' && row?.bankId === cashBank?._id
        ? Number(row.amount) || 0
        : 0,
    );

    const otherInTranfer = _.sumBy(paymentOther?.rows, (row) =>
      row?.typeTansaction === 'รายรับ' && row?.bankId !== cashBank?._id
        ? Number(row.amount) || 0
        : 0,
    );

    const otherOutCash = _.sumBy(paymentOther?.rows, (row) =>
      row?.typeTansaction === 'รายจ่าย' && row?.bankId === cashBank?._id
        ? Number(row.amount) || 0
        : 0,
    );

    const otherOutTranfer = _.sumBy(paymentOther?.rows, (row) =>
      row?.typeTansaction === 'รายจ่าย' && row?.bankId !== cashBank?._id
        ? Number(row.amount) || 0
        : 0,
    );

    const saleRows = _.filter(
      goldTransaction.rows,
      (row) => row?.gold_transaction_type === 'SALE',
    );
    const buyRows = _.filter(
      goldTransaction.rows,
      (row) => row?.gold_transaction_type === 'BUY',
    );

    // console.log('totals', otherIn, otherOut, saleRows, buyRows);
    const sumPayment = (rows, type) =>
      _.sumBy(rows, (row) =>
        row?.payment?.[type]?.status ? row?.payment?.[type]?.amount || 0 : 0,
      );

    const sale = {
      cash: sumPayment(saleRows, 'cash'),
      transfer: sumPayment(saleRows, 'transfer'),
      credit: sumPayment(saleRows, 'credit'),
    };

    const buy = {
      cash: sumPayment(buyRows, 'cash'),
      transfer: sumPayment(buyRows, 'transfer'),
      credit: sumPayment(buyRows, 'credit'),
    };

    return {
      sale: {
        เงินสด: sale.cash + otherInCash,
        โอน: sale.transfer + otherInTranfer,
        // เครดิต: sale.credit,
        เงินรวม:
          sale.cash +
          sale.transfer +
          sale.credit +
          otherInCash +
          otherInTranfer,
      },
      buy: {
        เงินสด: buy.cash + otherOutCash,
        โอน: buy.transfer + otherOutTranfer,
        // เครดิต: buy.credit,
        เงินรวม:
          buy.cash + buy.transfer + buy.credit + otherOutCash + otherOutTranfer,
      },
    };
  };

  const totals = calculateTotals();
  const summary = PAYMENT_TYPES.reduce(
    (acc, type) => ({
      ...acc,
      [type]: totals.sale[type] - totals.buy[type],
    }),
    {},
  );

  // Event handlers
  const handleTransaction = async (type) => {
    if (!amount || !listPayment) return;

    const payload = {
      date: dayjs(date).format('YYYY-MM-DD'),
      paymentlist: listPayment,
      amount: parseFloat(amount),
      typeTansaction: type,
      user: {
        id: me?.userData._id,
        firstname: me?.userData?.firstname,
        lastname: me?.userData?.lastname,
      },
    };

    await dispatch(actions.paymentOtherCreate(payload));
    setAmount('');
    setListPayment('');
    fetchData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ยืนยันการลบข้อมูล')) return;

    await dispatch(actions.paymentOtherDelete(id));
    fetchData();
  };

  const fetchData = () => {
    const dateStr = dayjs(date).format('YYYY-MM-DD');

    dispatch(actions.goldTransactionReset());
    dispatch(actions.bankAll());
    dispatch(actions.goldTransactionAll({ date: dateStr }));
    dispatch(
      actions.paymentOtherAll({
        name: '',
        page: 1,
        size: '',
        date: dateStr,
      }),
    );
  };

  useEffect(() => {
    fetchData();
  }, [date]);

  // Components
  const SummaryCard = ({ title, amount, type, className = '' }) => {
    const isPositive = amount >= 0;
    const bgColor =
      type === 'income'
        ? 'bg-gradient-to-br from-green-500 to-green-600'
        : type === 'outcome'
        ? 'bg-gradient-to-br from-red-500 to-red-600'
        : 'bg-gradient-to-br from-blue-500 to-blue-600';

    return (
      <div
        className={`${bgColor} rounded-2xl p-8 text-white shadow-xl transform hover:scale-105 transition-all duration-300 ${className}`}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold opacity-90">{title}</h3>
          <div
            className={`w-3 h-3 rounded-full ${
              isPositive ? 'bg-white bg-opacity-30' : 'bg-red-200'
            }`}
          ></div>
        </div>
        <div className="space-y-3">
          <div className="text-4xl font-bold">{formatNumber(amount)} บาท</div>
        </div>
      </div>
    );
  };

  const PaymentBreakdown = ({ data, title, colorScheme }) => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className={`${colorScheme} px-6 py-4`}>
        <h3 className="text-xl font-bold text-white">{title}</h3>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {PAYMENT_TYPES.slice(0, -1).map((type, index) => (
            <div
              key={index}
              className="flex justify-between items-center py-3 border-b border-gray-100 last:border-b-0"
            >
              <span className="text-gray-700 font-medium">{type}</span>
              <span className="text-xl font-bold text-gray-900">
                ฿{formatNumber(data[type])}
              </span>
            </div>
          ))}
          {/* <div className="flex justify-between items-center py-4 border-t-2 border-gray-200 bg-gray-50 -mx-6 px-6">
            <span className="text-lg font-bold text-gray-800">รวมทั้งหมด</span>
            <span className="text-2xl font-bold text-gray-900">
              ฿{formatNumber(data['เงินรวม'])}
            </span>
          </div> */}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <ViewTitle title={title} subtitle={subtitle} />

      <div className="w-full px-4 lg:px-8 py-6">
        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              การจัดการบัญชีรายวัน
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DesktopDatePicker
                label="เลือกวันที่"
                value={date}
                onChange={setDate}
                renderInput={(props) => <TextField {...props} size="medium" />}
              />
            </LocalizationProvider>
            <button
              onClick={() => history.push('/gold/detail/payment-other')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-md"
            >
              ธุรกรรมทางการเงิน
            </button>
          </div>
        </div>

        {/* Summary Cards - Top Priority */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          <SummaryCard
            title="ยอดรวมเงินเข้า"
            amount={totals.sale['เงินรวม']}
            type="income"
          />
          <SummaryCard
            title="ยอดรวมเงินออก"
            amount={totals.buy['เงินรวม']}
            type="outcome"
          />
          <SummaryCard
            title="ยอดคงเหลือสุทธิ"
            amount={summary['เงินรวม']}
            type="summary"
            className="md:col-span-1"
          />
        </div>

        {/* Payment Breakdown - Full Width */}
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-12">
          <PaymentBreakdown
            data={totals.sale}
            title="รายละเอียดเงินเข้า"
            colorScheme="bg-gradient-to-r from-green-500 to-green-600"
          />
          <PaymentBreakdown
            data={totals.buy}
            title="รายละเอียดเงินออก"
            colorScheme="bg-gradient-to-r from-red-500 to-red-600"
          />
          <PaymentBreakdown
            data={summary}
            title="สรุปยอดคงเหลือ"
            colorScheme="bg-gradient-to-r from-blue-500 to-blue-600"
          />
        </div>

        {/* Transaction History - Full Width Table */}
        {paymentOther?.rows?.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-gray-600 to-gray-700 px-8 py-6">
              <h3 className="text-2xl font-bold text-white">
                ประวัติทำรายการล่าสุด
              </h3>
              <p className="text-gray-200 mt-1">
                รายการทำรายการทั้งหมดในวันที่เลือก
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-8 py-4 text-left text-sm font-bold text-gray-600 uppercase tracking-wider">
                      วันที่
                    </th>
                    <th className="px-8 py-4 text-left text-sm font-bold text-gray-600 uppercase tracking-wider">
                      ชื่อทำรายการ
                    </th>
                    <th className="px-8 py-4 text-center text-sm font-bold text-gray-600 uppercase tracking-wider">
                      ประเภท
                    </th>
                    <th className="px-8 py-4 text-right text-sm font-bold text-gray-600 uppercase tracking-wider">
                      จำนวนเงิน
                    </th>
                    <th className="px-8 py-4 text-center text-sm font-bold text-gray-600 uppercase tracking-wider">
                      จัดการ
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paymentOther.rows.map((tx, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-8 py-6 text-sm text-gray-900 font-medium">
                        {dayjs(tx.date).format('DD/MM/YYYY')}
                      </td>
                      <td className="px-8 py-6 text-sm text-gray-900 font-medium">
                        {tx.paymentlist}
                      </td>
                      <td className="px-8 py-6 text-center">
                        <span
                          className={`inline-flex px-4 py-2 text-sm font-bold rounded-full ${
                            tx.typeTansaction === 'เพิ่มเงิน' ||
                            tx.typeTansaction === 'รายรับ'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {tx.typeTansaction}
                        </span>
                      </td>
                      <td className="px-8 py-6 text-right">
                        <span className="text-xl font-bold text-gray-900">
                          ฿{formatNumber(tx.amount)}
                        </span>
                      </td>
                      <td className="px-8 py-6 text-center">
                        <button
                          onClick={() => handleDelete(tx._id)}
                          className="bg-red-100 hover:bg-red-200 text-red-700 px-4 py-2 rounded-lg font-semibold transition-colors"
                        >
                          ลบ
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SumPaymentGold.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

SumPaymentGold.defaultProps = {
  title: 'ประวัติการชำระเงิน',
  subtitle: '',
};

export default SumPaymentGold;
